import type { Project, Skill, AccessRequest } from '../types/database';

const API_BASE = '/api/portfolio';
const ACCESS_API = '/api/access';

export interface PortfolioData {
  projects: Project[];
  skills: Skill[];
}

export const portfolioService = {
  // Get all projects
  async getProjects(): Promise<Project[]> {
    const response = await fetch(`${API_BASE}/projects`);
    if (!response.ok) {
      throw new Error('Failed to fetch projects');
    }
    return response.json();
  },

  // Get featured projects
  async getFeaturedProjects(): Promise<Project[]> {
    const response = await fetch(`${API_BASE}/projects/featured`);
    if (!response.ok) {
      throw new Error('Failed to fetch featured projects');
    }
    return response.json();
  },

  // Get single project by slug
  async getProjectBySlug(slug: string): Promise<Project | null> {
    const response = await fetch(`${API_BASE}/projects/${slug}`);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error('Failed to fetch project');
    }
    return response.json();
  },

  // Get all skills grouped by category
  async getSkills(): Promise<Record<string, Skill[]>> {
    const response = await fetch(`${API_BASE}/skills`);
    if (!response.ok) {
      throw new Error('Failed to fetch skills');
    }
    return response.json();
  },

  // Submit an access request
  async submitAccessRequest(request: {
    name: string;
    email: string;
    company?: string;
    reason: string;
    project_ids: string[];
  }): Promise<{ success: boolean; message: string; request?: Partial<AccessRequest> }> {
    const response = await fetch(`${ACCESS_API}/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to submit access request');
    }

    return data;
  },

  // Verify an access token
  async verifyAccessToken(token: string): Promise<{
    valid: boolean;
    email?: string;
    projectIds?: string[];
    expiresAt?: string;
    error?: string;
  }> {
    const response = await fetch(`${ACCESS_API}/verify-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });

    return response.json();
  },

  // Check access status by email
  async checkAccessStatus(email: string): Promise<{
    hasAccess: boolean;
    pendingRequest?: boolean;
    expiresAt?: string;
    projectIds?: string[];
    requestedAt?: string;
  }> {
    const response = await fetch(`${ACCESS_API}/status/${encodeURIComponent(email)}`);
    if (!response.ok) {
      throw new Error('Failed to check access status');
    }
    return response.json();
  },
};
